import React, { useEffect } from 'react';
import styled from 'styled-components';
import { HiOutlinePhone, HiPhoneXMark } from 'react-icons/hi2';
import { useVoiceCall } from '../../VoiceCallContext';
import { techColors, neonGlow } from '../../styles/TechRetroEffects';
import { useT } from '../../LocaleContext';

const RING_TIMEOUT_MS = 50000;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 500;
  padding: max(16px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left));
  box-sizing: border-box;
`;

const Card = styled.div`
  min-width: min(280px, 100%);
  max-width: 90vw;
  width: min(360px, 100%);
  padding: 24px;
  border-radius: 8px;
  background: ${techColors.darkBgSecondary};
  border: 1px solid ${techColors.borderSubtle};
  ${neonGlow(techColors.neonCyan, 'subtle')}
  text-align: center;
`;

const Title = styled.p`
  margin: 0 0 8px;
  color: ${techColors.textPrimary};
  font-size: 1.1em;
`;

const Subtitle = styled.p`
  margin: 0 0 20px;
  color: ${techColors.textSecondary};
  font-size: 0.85em;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const ActionButton = styled.button`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 1px solid ${techColors.borderSubtle};
  background: ${({ $danger }) => ($danger ? techColors.danger : techColors.darkBgTertiary)};
  color: ${techColors.textPrimary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4em;
  cursor: pointer;
`;

const DismissButton = styled.button`
  margin-top: 16px;
  border: none;
  background: transparent;
  color: ${techColors.textSecondary};
  font-size: 0.8em;
  cursor: pointer;
  text-decoration: underline;
`;

const IncomingCallOverlay = () => {
  const t = useT();
  const { isIncoming, activeCall, callProfiles, acceptCall, rejectCall } = useVoiceCall();

  useEffect(() => {
    if (!isIncoming) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        rejectCall();
      }
    };

    const timer = window.setTimeout(() => {
      rejectCall();
    }, RING_TIMEOUT_MS);

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isIncoming, rejectCall]);

  if (!isIncoming || !activeCall) {
    return null;
  }

  const peerName = callProfiles.peerName || activeCall.peer_name || t('call.unknownUser');

  return (
    <Overlay onClick={rejectCall}>
      <Card onClick={(event) => event.stopPropagation()}>
        <Title>{t('call.incoming')}</Title>
        <Subtitle>{peerName}</Subtitle>
        <Actions>
          <ActionButton
            type="button"
            onClick={() => acceptCall().catch(() => {})}
            aria-label={t('call.accept')}
          >
            <HiOutlinePhone />
          </ActionButton>
          <ActionButton type="button" $danger onClick={rejectCall} aria-label={t('call.reject')}>
            <HiPhoneXMark />
          </ActionButton>
        </Actions>
        <DismissButton type="button" onClick={rejectCall}>
          {t('call.dismiss')}
        </DismissButton>
      </Card>
    </Overlay>
  );
};

export default IncomingCallOverlay;
